import { isValid, parseISO } from "date-fns";
import * as yup from "yup";

import {
  MAX_MULTILINE_TEXT_LENGTH,
  MAX_TEXT_LENGTH,
} from "src/cases/caseView/constants";
import {
  CaseTemplateActionOutput,
  CaseTemplateFormFieldsInner,
} from "src/clients/case-management";
import { assertUnreachable } from "src/utils/typeUtils";

export type CaseActionFormValues = Partial<
  { action_key: string | null } & Record<string, unknown>
>;

/**
 * Get the base yup schema for a given form field type
 * @param field The form field definition
 * @returns The corresponding yup schema
 */
export const buildCaseActionFormSchema = (
  action?: CaseTemplateActionOutput,
) => {
  const formShape: Record<string, yup.AnySchema> = {
    action_key: yup.string().required("Please select an action"),
  };

  for (const field of action?.form?.fields ?? []) {
    const baseSchema = getBaseSchema(field);
    if (!baseSchema) continue;

    formShape[field.key] = field.required
      ? baseSchema.required(`${field.display_name} is required`)
      : baseSchema.nullable();
  }

  return yup.object(formShape) as yup.ObjectSchema<CaseActionFormValues>;
};

const getBaseSchema = (
  field: CaseTemplateFormFieldsInner,
): yup.AnySchema | null => {
  switch (field.type) {
    case "text": {
      const maxLength = field.display_as_textarea
        ? MAX_MULTILINE_TEXT_LENGTH
        : MAX_TEXT_LENGTH;
      return yup
        .string()
        .max(
          maxLength,
          `${field.display_name} must be at most ${maxLength.toLocaleString()} characters`,
        );
    }
    case "number":
      return yup
        .number()
        .transform((_, originalValue) => {
          return originalValue === "" ? undefined : Number(originalValue);
        })
        .typeError(`${field.display_name} must be a number`);

    case "integer":
      return yup
        .number()
        .transform((_, originalValue) => {
          return originalValue === "" ? undefined : Number(originalValue);
        })
        .integer()
        .typeError(`${field.display_name} must be a non-decimal number`);

    case "select":
      return yup.string();
    case "multi_select":
      return yup.array().of(yup.string());
    case "date":
      return yup
        .mixed()
        .test(
          "is-valid-date",
          `${field.display_name} must be a valid date`,
          (value): boolean => {
            if (isEmpty(value)) return true;
            const date = normalizeDate(value);
            return date !== null && isValid(date);
          },
        )
        .transform((_, originalValue) => {
          const date = normalizeDate(originalValue);
          return date && isValid(date) ? date.toISOString() : null;
        });
    default:
      assertUnreachable(field);
      return null;
  }
};

// Checks if a value is empty (null, undefined, or empty string after trimming).
const isEmpty = (value: unknown): boolean => {
  if (!value) return true;
  if (typeof value === "string") return value.trim() === "";
  return false;
};

// Normalizes a date value to a Date object or null.
const normalizeDate = (value: unknown) => {
  if (isEmpty(value)) return null;
  if (value instanceof Date) return value;

  if (typeof value === "string") {
    const date = parseISO(value.trim());
    return date;
  }

  return null;
};

/**
 * Convert a yup schema to a partial schema where all fields are optional
 * @param schema The original yup schema
 * @returns A new yup schema with all fields optional
 */
export const toPartialSchema = <T>(
  schema: yup.ObjectSchema<T extends yup.AnyObject ? T : never>,
  options?: {
    skip?: (keyof T)[];
  },
): yup.ObjectSchema<Partial<T>> => {
  const { fields } = schema;
  const partialShape: Record<string, yup.AnySchema> = {};

  for (const key in fields) {
    const field = fields[key] as yup.AnySchema;
    // Make each field not required
    partialShape[key] = options?.skip?.includes(key as keyof T)
      ? field
      : field.notRequired();
  }

  return yup.object(partialShape) as yup.ObjectSchema<Partial<T>>;
};
